import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Plus, Trash2, Save, Loader2, DollarSign, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import api from '../services/api';

const TokenPricingPage = () => {
  const { getThemeColors } = useSafeMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const colors = getThemeColors();

  const [baseCost, setBaseCost] = useState({ blended_cost_per_million: 5.0, target_roi_percent: 15 });
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiersChanged, setTiersChanged] = useState(false);
  const [costChanged, setCostChanged] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tierRes, costRes] = await Promise.all([
        api.get('/api/admin/system-settings/token-tiers'),
        api.get('/api/admin/system-settings/token-pricing').catch(() => ({ data: null })),
      ]);
      setTiers(tierRes.data.tiers || []);
      if (costRes.data) {
        setBaseCost({
          blended_cost_per_million: costRes.data.blended_cost_per_million ?? 5.0,
          target_roi_percent: costRes.data.target_roi_percent ?? 15,
        });
      }
    } catch (err) {
      console.error('Failed to load token pricing:', err);
      toast.error('Failed to load token pricing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Derived: sell price per 1M tokens
  const sellPricePer1M = baseCost.blended_cost_per_million * (1 + baseCost.target_roi_percent / 100);
  const margin = baseCost.target_roi_percent;

  // --- Base Cost ---
  const handleCostChange = (field, value) => {
    setBaseCost(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    setCostChanged(true);
  };

  const saveBaseCost = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/system-settings/token-pricing', baseCost);
      setCostChanged(false);
      toast.success('Base cost saved');
    } catch (err) {
      toast.error('Failed to save base cost');
    } finally {
      setSaving(false);
    }
  };

  // --- Tiers ---
  const updateTier = (idx, field, value) => {
    setTiers(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setTiersChanged(true);
  };

  const addTier = () => {
    const id = `tier_${Date.now()}`;
    setTiers(prev => [...prev, {
      tier_id: id, label: '', tokens: 0, volume_discount: 0,
      price_cents: 0, sort_order: prev.length, is_active: true,
    }]);
    setTiersChanged(true);
  };

  const removeTier = (idx) => {
    setTiers(prev => prev.filter((_, i) => i !== idx));
    setTiersChanged(true);
  };

  // Compute tier prices from base cost + volume discount
  // Discount only reduces the MARGIN portion, never eats into blended cost
  const computeTierPrice = (tier) => {
    if (!tier.tokens || tier.tokens <= 0) return { tierSellPrice: 0, tierCost: 0, marginAmount: 0, finalPrice: 0 };
    const tierSellPrice = sellPricePer1M * (tier.tokens / 1_000_000);
    const tierCost = baseCost.blended_cost_per_million * (tier.tokens / 1_000_000);
    const marginAmount = tierSellPrice - tierCost;
    const disc = tier.volume_discount || 0;
    // disc ranges 0→margin%. At max, discount removes 100% of margin → price = cost
    const discountFraction = margin > 0 ? disc / margin : 0;
    const finalPrice = tierSellPrice - (marginAmount * discountFraction);
    return { tierSellPrice, tierCost, marginAmount, finalPrice };
  };

  // Compute slider step so each notch = $0.25 price change
  const computeSliderStep = (marginAmount) => {
    if (marginAmount <= 0 || margin <= 0) return 1;
    // Each 1% of vol.disc changes price by (marginAmount / margin)
    // We want each step to change price by $0.25
    const priceChangePerPercent = marginAmount / margin;
    if (priceChangePerPercent <= 0) return 1;
    const step = 0.25 / priceChangePerPercent;
    // Clamp to reasonable bounds (avoid steps smaller than 0.01%)
    return Math.max(0.01, Math.min(step, margin));
  };

  const saveTiers = async () => {
    for (const t of tiers) {
      if (!t.tier_id || !t.label || !t.tokens) {
        toast.error('All tier fields are required (ID, label, tokens)');
        return;
      }
    }

    // Compute price_cents from current base cost + volume discount before saving
    const tiersToSave = tiers.map((t, i) => {
      const { finalPrice } = computeTierPrice(t);
      return {
        tier_id: t.tier_id,
        label: t.label,
        tokens: t.tokens,
        price_cents: Math.round(finalPrice * 100),
        volume_discount: t.volume_discount || 0,
        sort_order: t.sort_order ?? i,
        is_active: t.is_active,
      };
    });

    setSaving(true);
    try {
      await api.put('/api/admin/system-settings/token-tiers', tiersToSave);
      setTiersChanged(false);
      toast.success(`${tiersToSave.length} tiers saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tiers');
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)} data-testid="token-pricing-page">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Token Pricing</h1>
            <p className={cn("text-sm", colors.textMuted)}>
              Manage base token costs and buyup/topup tier pricing
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
            </div>
          ) : (
            <div className="space-y-6 max-w-5xl">

              {/* Section 1: Base Cost */}
              <Card className={cn("p-6", colors.bgSecondary, colors.border)} data-testid="base-cost-section">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className={cn("w-5 h-5", colors.textMuted)} />
                  <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Base Cost</h2>
                  <Badge variant="outline" className="text-xs">Foundation for pricing</Badge>
                </div>
                <p className={cn("text-sm mb-4", colors.textMuted)}>
                  Your blended cost across all AI models. Target margin sets your sell price.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm">Blended Cost / 1M Tokens</Label>
                    <div className="relative">
                      <span className={cn("absolute left-3 top-1/2 -translate-y-1/2 text-sm", colors.textMuted)}>$</span>
                      <Input
                        data-testid="blended-cost-input"
                        type="number"
                        step="0.01"
                        value={baseCost.blended_cost_per_million}
                        onChange={e => handleCostChange('blended_cost_per_million', e.target.value)}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Target Margin</Label>
                    <div className="relative">
                      <Input
                        data-testid="target-margin-input"
                        type="number"
                        step="1"
                        value={baseCost.target_roi_percent}
                        onChange={e => handleCostChange('target_roi_percent', e.target.value)}
                        className="pr-7"
                      />
                      <span className={cn("absolute right-3 top-1/2 -translate-y-1/2 text-sm", colors.textMuted)}>%</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Sell Price / 1M Tokens</Label>
                    <div className={cn("h-10 flex items-center px-3 rounded-md text-sm font-mono", colors.bgPrimary, colors.border, "border")}>
                      ${sellPricePer1M.toFixed(2)}
                    </div>
                  </div>
                </div>

                {costChanged && (
                  <div className="mt-4 flex justify-end">
                    <Button
                      data-testid="save-base-cost-btn"
                      onClick={saveBaseCost}
                      disabled={saving}
                      size="sm"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                      Save Base Cost
                    </Button>
                  </div>
                )}
              </Card>

              {/* Section 2: Buyup Tiers */}
              <Card className={cn("p-6", colors.bgSecondary, colors.border)} data-testid="buyup-tiers-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Layers className={cn("w-5 h-5", colors.textMuted)} />
                    <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Token Tiers</h2>
                    <Badge variant="outline" className="text-xs">{tiers.length} tiers</Badge>
                  </div>
                  <Button
                    data-testid="add-tier-btn"
                    onClick={addTier}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Tier
                  </Button>
                </div>
                <p className={cn("text-sm mb-4", colors.textMuted)}>
                  Token packages available for purchase. Sell price derived from base cost. Volume discount bounded at 0–{margin}%.
                </p>

                {tiers.length === 0 ? (
                  <p className={cn("text-sm py-8 text-center", colors.textMuted)}>
                    No tiers configured. Add one to get started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="grid grid-cols-[80px_1fr_100px_100px_140px_100px_56px_36px] gap-2 px-2 items-end">
                      <Label className="text-xs">Tier ID</Label>
                      <Label className="text-xs">Label</Label>
                      <Label className="text-xs">Tokens</Label>
                      <Label className="text-xs">Sell Price*</Label>
                      <Label className="text-xs">Vol. Disc.</Label>
                      <Label className="text-xs">Price ($)</Label>
                      <Label className="text-xs text-center">Active</Label>
                      <span />
                    </div>

                    {tiers.map((tier, idx) => {
                      const { tierSellPrice, marginAmount, finalPrice } = computeTierPrice(tier);
                      const disc = tier.volume_discount || 0;
                      const sliderStep = computeSliderStep(marginAmount);

                      return (
                        <div
                          key={tier.tier_id || idx}
                          className={cn(
                            "grid grid-cols-[80px_1fr_100px_100px_140px_100px_56px_36px] gap-2 items-center p-2 rounded-md",
                            colors.bgPrimary, colors.border, "border",
                            !tier.is_active && "opacity-50"
                          )}
                          data-testid={`tier-row-${tier.tier_id}`}
                        >
                          {/* Tier ID */}
                          <Input
                            className="text-xs h-8"
                            value={tier.tier_id}
                            onChange={e => updateTier(idx, 'tier_id', e.target.value)}
                            placeholder="500k"
                            data-testid={`tier-id-${idx}`}
                          />
                          {/* Label */}
                          <Input
                            className="text-xs h-8"
                            value={tier.label}
                            onChange={e => updateTier(idx, 'label', e.target.value)}
                            placeholder="500K Tokens"
                            data-testid={`tier-label-${idx}`}
                          />
                          {/* Tokens */}
                          <Input
                            className="text-xs h-8"
                            type="number"
                            value={tier.tokens}
                            onChange={e => updateTier(idx, 'tokens', parseInt(e.target.value) || 0)}
                            data-testid={`tier-tokens-${idx}`}
                          />
                          {/* Sell Price (read-only, derived from base cost) */}
                          <div
                            className={cn("h-8 flex items-center px-2 rounded text-xs font-mono", colors.textMuted)}
                            data-testid={`tier-sell-price-${idx}`}
                          >
                            ${tierSellPrice.toFixed(2)}
                          </div>
                          {/* Volume Discount */}
                          <div className="flex items-center gap-1.5">
                            <Slider
                              value={[disc]}
                              min={0}
                              max={margin > 0 ? margin : 1}
                              step={sliderStep}
                              onValueChange={([v]) => updateTier(idx, 'volume_discount', v)}
                              className="flex-1"
                              data-testid={`tier-discount-slider-${idx}`}
                            />
                            <span className={cn("text-xs font-mono w-10 text-right tabular-nums", colors.textMuted)}>
                              {disc.toFixed(1)}%
                            </span>
                          </div>
                          {/* Final Price (read-only) */}
                          <div
                            className={cn("h-8 flex items-center px-2 rounded text-xs font-semibold font-mono", colors.textPrimary)}
                            data-testid={`tier-final-price-${idx}`}
                          >
                            ${finalPrice.toFixed(2)}
                          </div>
                          {/* Active */}
                          <div className="flex justify-center">
                            <Switch
                              checked={tier.is_active}
                              onCheckedChange={v => updateTier(idx, 'is_active', v)}
                              data-testid={`tier-active-${idx}`}
                            />
                          </div>
                          {/* Delete */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeTier(idx)}
                            data-testid={`tier-delete-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        </div>
                      );
                    })}

                    <p className={cn("text-xs pt-2", colors.textMuted)}>
                      * Sell Price = (Sell/1M) x (Tokens / 1M). Vol. Disc. only reduces margin — at max ({margin}%) you're at blended cost. Each slider notch = $0.25.
                    </p>
                  </div>
                )}

                {tiersChanged && (
                  <div className="mt-4 flex justify-end">
                    <Button
                      data-testid="save-tiers-btn"
                      onClick={saveTiers}
                      disabled={saving}
                      size="sm"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                      Save Tiers
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TokenPricingPage;
